import { z } from "zod";
import { actorSchema, credentialScopeSchema, idSchema, instantSchema, roleSchema } from "@amber/contracts";

const reasonSchema = z.string().trim().min(1);
const verifierSchema = z.string().min(1);
const domainNameSchema = z
  .string()
  .regex(/^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "expected a lower-case domain name");

export const USER_STATES = ["active", "deactivated"] as const;
export const userStateSchema = z.enum(USER_STATES);

const userBase = {
  id: idSchema("usr"),
  google_subject: z.string().min(1).max(255),
  email: z.email(),
  display_name: z.string().trim().min(1),
  role: roleSchema,
  created_at: instantSchema,
  created_via: z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("invitation"), invitation_id: idSchema("inv") }),
    z.strictObject({ kind: z.literal("auto_join_domain"), domain_id: idSchema("adm") }),
  ]),
  revision: z.number().int().positive(),
};

export const userRecordSchema = z.discriminatedUnion("state", [
  z.strictObject({ ...userBase, state: z.literal("active") }),
  z.strictObject({
    ...userBase,
    state: z.literal("deactivated"),
    deactivated: z.strictObject({
      at: instantSchema,
      by: actorSchema,
      reason: reasonSchema,
      audit_id: idSchema("aud"),
    }),
  }),
]);
export type UserRecord = z.infer<typeof userRecordSchema>;

export const INVITATION_STATES = ["pending", "used", "revoked"] as const;
export const invitationStateSchema = z.enum(INVITATION_STATES);

const invitationBase = {
  id: idSchema("inv"),
  email: z.email(),
  role: roleSchema,
  note: z.string().trim().min(1).nullable(),
  created_by: actorSchema,
  created_at: instantSchema,
  expires_at: instantSchema,
};

export const invitationRecordSchema = z.discriminatedUnion("state", [
  z.strictObject({ ...invitationBase, state: z.literal("pending"), used: z.null() }),
  z.strictObject({
    ...invitationBase,
    state: z.literal("used"),
    used: z.strictObject({ at: instantSchema, user_id: idSchema("usr") }),
  }),
  z.strictObject({
    ...invitationBase,
    state: z.literal("revoked"),
    used: z.null(),
    revoked: z.strictObject({ at: instantSchema, by: actorSchema, reason: reasonSchema }),
  }),
]);
export type InvitationRecord = z.infer<typeof invitationRecordSchema>;

export const autoJoinDomainRecordSchema = z.strictObject({
  id: idSchema("adm"),
  domain: domainNameSchema,
  created_by: actorSchema,
  created_at: instantSchema,
});
export type AutoJoinDomainRecord = z.infer<typeof autoJoinDomainRecordSchema>;

export const MACHINE_CREDENTIAL_STATES = ["active", "revoked", "expired"] as const;
export const machineCredentialStateSchema = z.enum(MACHINE_CREDENTIAL_STATES);

const machineCredentialBase = {
  id: idSchema("mcr"),
  name: z.string().trim().min(1),
  scopes: z
    .array(credentialScopeSchema)
    .min(1)
    .refine((scopes) => new Set(scopes).size === scopes.length, "scopes must be unique"),
  secret_verifier: verifierSchema,
  expires_at: instantSchema.nullable(),
  last_used_at: instantSchema.nullable(),
  issued_by: actorSchema,
  issued_at: instantSchema,
};

export const machineCredentialRecordSchema = z.discriminatedUnion("state", [
  z.strictObject({ ...machineCredentialBase, state: z.literal("active") }),
  z.strictObject({
    ...machineCredentialBase,
    state: z.literal("revoked"),
    revoked: z.strictObject({ at: instantSchema, by: actorSchema, reason: reasonSchema, audit_id: idSchema("aud") }),
  }),
  z.strictObject({ ...machineCredentialBase, state: z.literal("expired"), expires_at: instantSchema }),
]);
export type MachineCredentialRecord = z.infer<typeof machineCredentialRecordSchema>;

export const exampleRecordSchemas: Record<string, z.ZodType> = {
  "users.json": userRecordSchema,
  "invitations.json": invitationRecordSchema,
  "auto-join-domains.json": autoJoinDomainRecordSchema,
  "machine-credentials.json": machineCredentialRecordSchema,
};
